









































import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { VueEditor } from 'vue2-editor'
import { validateEmail } from '@/utils/common'
import { LayoutEmailFormModel } from '@/types/interfaces/LayoutEmailFormModel'
import sanitizarHtmlParaEditor, { possuiEstruturaNaoSuportada } from './sanitizarHtmlParaEditor'
import migrarBlocoAssinaturaFloat, { possuiBlocoAssinaturaFloat } from './migrarBlocoAssinaturaFloat'
import normalizarClassesQuillParaHtmlPortavel from './normalizarClassesQuillParaHtmlPortavel'
import './quillImageComEstilo'
import { instalarInnerHtmlCompativelComCsp } from './estilosInlineCsp'
import { EDITOR_TOOLBAR, CUSTOM_MODULES, EDITOR_OPTIONS } from './quillEditorConfig'
import {
  aplicarTooltipsToolbar,
  configurarTeclasTabelaAssinatura,
  inserirBlocoAssinatura as inserirBlocoAssinaturaNoQuill,
} from './quillBlocoAssinaturaHandlers'

type CampoEditavel = 'assunto' | 'corpo' | 'rodape'

@Component({
  components: { VueEditor },
})
export default class LayoutEmailForm extends Vue {
  @Prop({ required: true }) value: LayoutEmailFormModel

  localModel: LayoutEmailFormModel = this.sanitizarModel(this.value)
  campoFocado: CampoEditavel = 'corpo'
  avisoPerdaCorpo: boolean = false
  avisoPerdaRodape: boolean = false

  editorToolbar = EDITOR_TOOLBAR
  customModules = CUSTOM_MODULES
  editorOptions = EDITOR_OPTIONS

  get requiredRules() {
    return [(v: string) => !!v || 'Campo obrigatório.']
  }

  get emailRules() {
    return [
      (v: string) => !!v || 'Campo obrigatório.',
      (v: string) => validateEmail(v) || 'E-mail inválido.',
    ]
  }

  // immediate: true garante que o pai (preview/salvar) já recebe a versão portável desde a
  // abertura do formulário, sem depender de o usuário editar algo primeiro.
  @Watch('localModel', { deep: true, immediate: true })
  onLocalModelChange(value: LayoutEmailFormModel) {
    this.$emit('input', this.valorPortavel(value))
  }

  mounted() {
    this.configurarEditor('corpoEditor')
    this.configurarEditor('rodapeEditor')

    // O vue2-editor seta quill.root.innerHTML diretamente no conteúdo inicial (fora da API do
    // Quill), então o Delta interno do Quill só sincroniza com isso via MutationObserver — uma
    // microtask que ainda não rodou no momento síncrono do mounted(). Sem o setTimeout,
    // quill.getText() aqui pegaria o editor ainda "vazio", disparando falso positivo.
    setTimeout(() => this.verificarPerdaConteudo(), 0)
  }

  private configurarEditor(editorRef: string) {
    const quill = this.obterQuill(editorRef)

    if (!quill) {
      return
    }

    instalarInnerHtmlCompativelComCsp(quill.root)

    // O vue2-editor carrega o conteúdo inicial no mounted dele, antes deste ponto; no Firefox o
    // style bloqueado já foi descartado, então o valor é recarregado pelo setter instalado acima.
    const { value: conteudoInicial } = this.$refs[editorRef] as any

    if (conteudoInicial) {
      quill.root.innerHTML = conteudoInicial
    }

    quill.update('silent')
    aplicarTooltipsToolbar(quill)
    configurarTeclasTabelaAssinatura(quill)
  }

  private obterQuill(editorRef: string): any {
    const editor: any = this.$refs[editorRef]
    return editor && editor.quill
  }

  private verificarPerdaConteudo() {
    this.avisoPerdaCorpo = this.houvePerdaSignificativa(this.value.emailCorpo, 'corpoEditor')
    this.avisoPerdaRodape = this.houvePerdaSignificativa(this.value.emailRodape, 'rodapeEditor')
  }

  private houvePerdaSignificativa(htmlOriginal: string, editorRef: string): boolean {
    // O sanitizador/migrador (sanitizarModel) já reconstrói tag antiga/não suportada e bloco de
    // assinatura em float para algo que o editor representa sem perder texto — por isso, a partir
    // daqui, o texto quase nunca "encolhe" mesmo quando o HTML original era antigo. O aviso então
    // depende de detectar a estrutura antiga diretamente no HTML original, não só medir perda de texto.
    if (possuiEstruturaNaoSuportada(htmlOriginal) || possuiBlocoAssinaturaFloat(htmlOriginal)) {
      return true
    }

    const textoOriginal = this.textoPlano(htmlOriginal)

    if (!textoOriginal) {
      return false
    }

    const quill = this.obterQuill(editorRef)
    const textoAtual = quill ? quill.getText().replace(/\s+/g, ' ').trim() : ''
    const limiar = textoOriginal.length * 0.85

    return textoAtual.length < limiar
  }

  private textoPlano(html: string): string {
    if (!html) {
      return ''
    }

    const doc = new DOMParser().parseFromString(html, 'text/html')

    return (doc.body.textContent || '').replace(/\s+/g, ' ').trim()
  }

  setValue(value: LayoutEmailFormModel) {
    this.localModel = this.sanitizarModel(value)

    setTimeout(() => {
      this.avisoPerdaCorpo = this.houvePerdaSignificativa(value.emailCorpo, 'corpoEditor')
      this.avisoPerdaRodape = this.houvePerdaSignificativa(value.emailRodape, 'rodapeEditor')
    }, 0)
  }

  getValue(): LayoutEmailFormModel {
    return this.valorPortavel(this.localModel)
  }

  // O HTML do Quill (v-model de localModel) usa classes como ql-indent-N/ql-align-* para recuo e
  // alinhamento, com estilo definido só dentro do próprio editor (quill.core.css escopado em
  // ".ql-editor") — fora dali (prévia, e-mail enviado) a classe não faz nada e o recuo some.
  // Por isso quem consome o valor para fora do editor recebe a versão com style inline
  // equivalente; localModel (ligado ao Quill em edição) nunca é sobrescrito com isso.
  private valorPortavel(value: LayoutEmailFormModel): LayoutEmailFormModel {
    return {
      ...value,
      emailCorpo: normalizarClassesQuillParaHtmlPortavel(value.emailCorpo),
      emailRodape: normalizarClassesQuillParaHtmlPortavel(value.emailRodape),
    }
  }

  private sanitizarModel(value: LayoutEmailFormModel): LayoutEmailFormModel {
    return {
      ...value,
      emailCorpo: sanitizarHtmlParaEditor(migrarBlocoAssinaturaFloat(value.emailCorpo)),
      emailRodape: sanitizarHtmlParaEditor(migrarBlocoAssinaturaFloat(value.emailRodape)),
    }
  }

  validate(): boolean {
    return (this.$refs.form as any).validate()
  }

  inserirToken(token: string): boolean {
    if (this.campoFocado === 'assunto') {
      return false
    }

    const texto = `{{ p.${token} }}`
    const editorRef = this.campoFocado === 'rodape' ? 'rodapeEditor' : 'corpoEditor'
    const campo = this.campoFocado === 'rodape' ? 'emailRodape' : 'emailCorpo'

    this.inserirTextoNoEditor(editorRef, campo, texto)

    return true
  }

  inserirBlocoAssinatura() {
    const quill = this.obterQuill('rodapeEditor')

    if (!quill) {
      return
    }

    inserirBlocoAssinaturaNoQuill(quill)
  }

  private inserirTextoNoEditor(editorRef: string, campo: 'emailCorpo' | 'emailRodape', texto: string) {
    const quill = this.obterQuill(editorRef)

    if (!quill) {
      this.localModel[campo] = `${this.localModel[campo] || ''} ${texto}`
      return
    }

    const selection = quill.getSelection(true)
    const index = selection ? selection.index : quill.getLength()

    quill.insertText(index, texto, 'user')
    quill.setSelection(index + texto.length)
  }
}
